import React, { useEffect, useState, Fragment, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./CreateJob.scss";
import useStyles from "./CreateJobStyle";
import {
  AccordionDetails,
  Grid,
  AccordionSummary,
  Accordion,
  Typography,
  RadioGroup,
  Radio,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import {
  RecurringTypesArray,
  repeatDays,
} from "../../constants/recurringTypes";
import Dropdown from "../Dropdown/Dropdown";
import DateTimePickerInput from "../TemplatesCommonMethods/DateTimePickerInput";
import DateTimeFormatter, {
  getDayName,
  getMonthName,
  getOrdinalDay,
  getOrdinalWeek,
} from "../../utils/DateFormatter";
import Input from "../Input/Input";
import useLocalStorage from "../../hooks/localStorage";
import moment from "moment";
import useToast from "../Toast/hooks/useToast";

const CreateRecurringJob = (props?: any) => {
  const toaster = useToast();
  const { t, i18n } = useTranslation(["Common", "Createjob"]);
  const loginData: any = useLocalStorage.getItem("loginData");
  const recurringTypesArray = RecurringTypesArray(t);
  const classes = useStyles();
  const [recurringName, setRecurringName] = useState(
    props.repeatObj.recurringName
  );
  const [isRecurringEnabled, setIsRecurringEnabled] = useState(
    props.repeatObj.isRecurringEnabled
  );
  const [selectedRecurringType, setSelectedRecurringType] = useState(
    props.repeatObj.cycle
  );
  const [selectedDays, setSelectedDays]: any = useState({
    "0": true,
    "1": true,
    "2": true,
    "3": true,
    "4": true,
    "5": true,
    "6": true,
  });
  const [selectedFortnightlyDays, setSelectedFortnightlyDays]: any = useState({
    0: false,
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
    6: false,
  });
  const [monthlyYearlyDate, setMonthlyYearlyDate]: any = useState(null);
  const [scheduleEndCriteria, setScheduleEndCriteria] = useState(0);
  const [recurrenceOption, setRecurrenceOption] = useState(
    props.repeatObj.recurrence_pattern?.type?.toString() || "1"
  );
  const scrollRef: any = useRef(null);
  const [endDate, setEndDate]: any = useState("");
  const [occurrences, setOccurrences] = useState();
  const [scrollValue, setScrollValue]: any = useState();
  const handleChangeRecurringToggle = (evt: any) => {
    if (evt && evt.target.checked) {
      props.openManuallyAccordion("accordion-recurring");
    } else {
      props.openManuallyAccordion("");
    }
    setIsRecurringEnabled(evt.target.checked);
    if (evt.target.checked) {
      setScrollValue(true);
    } else {
      setScrollValue(false);
    }
    updateRepeatObj({
      key: "isRecurringEnabled",
      value: evt.target.checked,
    });
  };
  const updateSelectedDays = (dayId: number) => {
    let newData = { ...selectedDays };
    newData[dayId] = newData[dayId] === true ? false : true;
    const isAnyDaySelected = Object.values(newData).some((day) => day === true);
    if (!isAnyDaySelected) {
      toaster.addToast({
        message: "Select at least one day to continue.",
        timeout: 3000,
        type: "error",
      });
      return;
    }
    setSelectedDays(newData);
    updateRepeatObj({
      key: "cycle_def",
      value: newData,
    });
  };

  const updateSelectedFortnightlyDays = (id: number) => {
    let newData: any = {
      0: false,
      1: false,
      2: false,
      3: false,
      4: false,
      5: false,
      6: false,
    };
    newData[id] = true;
    setSelectedFortnightlyDays(newData);
    updateRepeatObj({
      key: "cycle_def",
      value: newData,
    });
  };

  const handleScheduleCriteriaChange = (evt: any) => {
    setScheduleEndCriteria(evt.target.value);
    updateRepeatObj({
      key: "scheduleEndCriteria",
      value: evt.target.value,
    });
  };
  const updateRepeatObj = (data: any) => {
    let tempData: any = props.repeatObj;
    tempData[data.key] = data.value;
    props.updateRepeatObj(tempData);
  };

  // Utility function to parse date strings or Date objects
  const parseDate = (dateInput: any, defaultDate = new Date()) => {
    if (dateInput instanceof Date) return dateInput;
    if (typeof dateInput !== "string") {
      console.error("Invalid date type:", dateInput);
      return defaultDate;
    }

    const isISOFormat = dateInput.includes("T") && dateInput.includes("Z");
    if (isISOFormat) return new Date(dateInput);

    return moment(dateInput, [
      "DD-MM-YYYY hh:mm A",
      "ddd MMM DD YYYY HH:mm:ss [GMT]Z",
      "MMM DD - HH:mm ,YYYY",
      "MMM DD - hh:mm A ,YYYY",
      "MM-DD-YYYY hh:mm A",
      "DD-MM-YYYY HH:mm",
      "MM-DD-YYYY HH:mm",
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DD hh:mm A",
    ]).toDate();
  };

  // Utility function to convert end date based on time format
  const convertEndDate = (
    endDate: any,
    loginData: any,
    DateTimeFormatter: any
  ) => {
    if (!loginData.time_format && !DateTimeFormatter.checkIfTimeInTz) {
      return endDate;
    }
    return DateTimeFormatter.converDateTimeTo24HoursWithoutUTC(endDate);
  };

  // Utility function to handle fortnightly date logic
  const handleFortnightlyDate = (
    repeatObj: any,
    selectedFortnightlyDays: { [key: number]: boolean },
    setRecurrenceOption: (value: any) => void,
    updateSelectedFortnightlyDays: (id: number) => void,
    updateRepeatObj: (data: any) => void
  ) => {
    setRecurrenceOption(repeatObj?.recurrence_pattern?.type?.toString() || "1");
    const selectedDate = parseDate(
      repeatObj.pickupDate || repeatObj.deliveryDate
    );

    if (
      selectedDate &&
      !isNaN(selectedDate.getTime()) &&
      !Object.values(selectedFortnightlyDays).some((val) => val === true)
    ) {
      const dayOfWeek = selectedDate.getDay();
      updateSelectedFortnightlyDays(dayOfWeek);
      updateRepeatObj({
        key: "pattern_date",
        value: repeatObj?.pattern_date
          ? moment(repeatObj.pattern_date).format("YYYY-MM-DD")
          : selectedDate,
      });
    }
  };

  // Utility function to handle monthly/yearly date logic
  const handleMonthlyYearlyDate = (
    repeatObj: any,
    setRecurrenceOption: (value: any) => void,
    updateRepeatObj: (data: any) => void,
    setMonthlyYearlyDate: (value: any) => void,
    recurrenceOption: any
  ) => {
    setRecurrenceOption(repeatObj?.recurrence_pattern?.type?.toString() || "1");
    const selectedDate = parseDate(
      repeatObj.pickupDate || repeatObj.deliveryDate
    );

    if (selectedDate && !isNaN(selectedDate.getTime())) {
      const dayOfMonth = selectedDate.getDate();
      const weekOfMonth = Math.ceil(dayOfMonth / 7);
      const dayOfWeek = selectedDate.getDay();

      updateRepeatObj({
        key: "pattern_date",
        value: repeatObj?.pattern_date
          ? moment(repeatObj.pattern_date).format("YYYY-MM-DD")
          : selectedDate,
      });
      updateRepeatObj({
        key: "recurrence_pattern",
        value: {
          type: parseInt(recurrenceOption),
          value: parseInt(recurrenceOption) === 1 ? dayOfMonth : weekOfMonth,
          day: parseInt(recurrenceOption) === 1 ? null : dayOfWeek,
        },
      });
      setMonthlyYearlyDate(
        moment(
          repeatObj?.pattern_date ? repeatObj?.pattern_date : selectedDate
        ).format("YYYY-MM-DD")
      );
    }
  };

  useEffect(() => {
    // Set initial recurring type and cycle definition
    setSelectedRecurringType(props.repeatObj.cycle);
    if (props.repeatObj.cycle_def) {
      setSelectedDays(props.repeatObj.cycle_def);
    }

    // Handle end date conversion
    // const convertedEndDate = convertEndDate(
    //   props.repeatObj.endDate,
    //   loginData,
    //   DateTimeFormatter
    // );
    const convertedEndDate = parseDate(
      props.repeatObj.endDate || new Date(),
      new Date()
    );
    setEndDate(convertedEndDate);

    // Set schedule end criteria and occurrences
    setScheduleEndCriteria(props.repeatObj.scheduleEndCriteria);
    setOccurrences(props.repeatObj.occurrences);

    // Handle fortnightly cycle (cycle === 10)
    if (props?.repeatObj?.cycle === 10) {
      const savedFortnightlyDays = props.repeatObj.cycle_def;
      const hasSavedFortnightlyDay =
        savedFortnightlyDays &&
        Object.values(savedFortnightlyDays).some((day) => day === true);

      if (hasSavedFortnightlyDay) {
        setSelectedFortnightlyDays(savedFortnightlyDays);
      } else {
        handleFortnightlyDate(
          props.repeatObj,
          selectedFortnightlyDays,
          setRecurrenceOption,
          updateSelectedFortnightlyDays,
          updateRepeatObj
        );
      }
    }
    // Handle monthly/yearly cycles (cycle === 8 or 9)
    else if (
      props?.repeatObj &&
      (props.repeatObj.cycle === 8 || props.repeatObj.cycle === 9)
    ) {
      handleMonthlyYearlyDate(
        props.repeatObj,
        setRecurrenceOption,
        updateRepeatObj,
        setMonthlyYearlyDate,
        recurrenceOption
      );
    }
  }, [props.repeatObj, props.expanded, selectedRecurringType]);

  const scrollToBottom = (scrollOffset?: any) => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };
  useEffect(() => {
    if (scrollValue) {
      setTimeout(() => {
        scrollToBottom();
      }, 200);
    }
  }, [scrollValue]);
  return (
    <>
      <div>
        <div ref={scrollRef}>{""}</div>

        <div style={{ marginBottom: "10px" }}>
          {t("Createjob:Do you want to create recurring jobs?")}
          <Switch
            onClick={(event) => event.stopPropagation()}
            checked={isRecurringEnabled}
            onChange={handleChangeRecurringToggle}
            value="checked"
            color="primary"
          />
        </div>

        {isRecurringEnabled ? (
          <>
            <div>
              {/* <Accordion
            className={classes.accordionWrapper}
            expanded={props.expanded === "accordion-recurring" ? true : false}
            onChange={() => {
              if (props.expanded !== "accordion-recurring") {
                handleChangeRecurringToggle({ target: { checked: true } });
                props.handleChange("accordion-recurring");
              } else {
                props.openManuallyAccordion("");
              }
            }}
          >
            <AccordionSummary
              className={classes.expanded}
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            > */}
              <Typography
                style={{
                  fontWeight: "bold",
                  fontSize: "23px",
                  marginBottom: "1.2rem",
                }}
                className="CreateJob__form__task-accordion__recurring-type-heading"
              >
                {t("Createjob:Recurring Rule")}
              </Typography>
              {/* </AccordionSummary>
            <AccordionDetails className={classes.padding0}> */}
              <Grid container spacing={2} style={{ marginTop: "4px" }}>
                <Grid item xs={6}>
                  <div className="CreateJob__form__recurring__scheduleName">
                    <Input
                      type="text"
                      id={"recurring-name"}
                      name={"recurring-name"}
                      onchange={(evt: any) => {
                        setRecurringName(evt.target.value);
                        updateRepeatObj({
                          key: "recurringName",
                          value: evt.target.value,
                        });
                      }}
                      value={recurringName}
                      label={t("Createjob:Rule Name") + "*"}
                    ></Input>
                  </div>
                </Grid>

                <Grid
                  xs={6}
                  className="CreateJob__form__recurring__scheduleCycle"
                >
                  <Dropdown
                    labelName={t("Createjob:Recurrence Cycle")}
                    MenuItemMaxWidth="100%"
                    onChange={(event: any) => {
                      setSelectedRecurringType(event.target.value);
                      updateRepeatObj({
                        key: "cycle",
                        value: event.target.value,
                      });
                    }}
                    translationComponentName=""
                    selectedValue={selectedRecurringType}
                    itemList={recurringTypesArray}
                    uniqueId="value"
                    label="name"
                  ></Dropdown>
                </Grid>
                {selectedRecurringType === 1 ? (
                  <Grid item xs={6} className={"CreateJob__repeatOn"}>
                    <div
                      style={{ marginTop: 0 }}
                      className={"CreateJob__repeatOn__repeat-heading"}
                    >
                      {t("Createjob:Repeat On")} :
                    </div>

                    {repeatDays.map((day: any) => (
                      <span
                        onClick={() => {
                          updateSelectedDays(day?.id);
                        }}
                        className={
                          selectedDays[day?.id] === true
                            ? "CreateJob__repeatOn__repeat-days-selected"
                            : "CreateJob__repeatOn__repeat-days"
                        }
                        key={day?.id}
                      >
                        {day.name}
                      </span>
                    ))}
                  </Grid>
                ) : selectedRecurringType === 8 ||
                  selectedRecurringType === 9 ? (
                  <Grid item xs={12}>
                    <Grid
                      container
                      spacing={2}
                      alignItems="flex-start"
                      direction="column-reverse"
                      style={{ marginTop: "4px" }}
                    >
                      <Grid item xs={6}>
                        <DateTimePickerInput
                          label={t("Createjob:Select Date")}
                          type={2}
                          className={
                            "CreateJob__form__task-accordion__date-input"
                          }
                          disablePast={true}
                          showIcon={true}
                          value={monthlyYearlyDate || null}
                          onChange={(value: any) => {
                            const newDate = new Date(value);
                            newDate.setHours(23);
                            newDate.setMinutes(59);
                            newDate.setSeconds(59);
                            setMonthlyYearlyDate(
                              moment(newDate).format("YYYY-MM-DD")
                            );

                            // Calculate day, month, week, and weekday for dynamic labels
                            const dayOfMonth = newDate.getDate();
                            const weekOfMonth = Math.ceil(dayOfMonth / 7);
                            const dayOfWeek = newDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
                            updateRepeatObj({
                              key: "pattern_date",
                              value: moment(newDate).format("YYYY-MM-DD"),
                            });
                            updateRepeatObj({
                              key: "pickupDate",
                              value: newDate,
                            });
                            updateRepeatObj({
                              key: "recurrence_pattern",
                              value: {
                                type: parseInt(recurrenceOption),
                                value:
                                  parseInt(recurrenceOption) === 1
                                    ? newDate.getDate()
                                    : weekOfMonth,
                                day:
                                  parseInt(recurrenceOption) === 1
                                    ? null
                                    : dayOfWeek,
                              },
                            });
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="body1"
                          style={{
                            marginBottom: "3px",
                            marginLeft: "0.2rem",
                          }}
                          className={
                            "CreateJob__form__recurring__instruction-text"
                          }
                        >
                          Select a start date
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} style={{ marginTop: "0.8rem" }}>
                      <Typography variant="body1">
                        {t("Createjob:Recurrence Options")}
                      </Typography>
                      <RadioGroup
                        defaultValue={recurrenceOption}
                        onChange={(event) => {
                          const newValue = event.target.value;
                          setRecurrenceOption(newValue);
                          const newType = newValue === "1" ? 1 : 2; // Type 1 for first option, 2 for second option
                          const selectedDate = moment(monthlyYearlyDate);
                          const dayOfMonth = Number(selectedDate.format("DD"));
                          const weekOfMonth = Math.ceil(dayOfMonth / 7);
                          const dayOfWeek = selectedDate.day();
                          updateRepeatObj({
                            key: "recurrence_pattern",
                            value: {
                              type: newType,
                              value:
                                newType === 1
                                  ? Number(selectedDate.format("DD"))
                                  : weekOfMonth,
                              day: newType === 1 ? null : dayOfWeek,
                            },
                          });
                        }}
                        aria-label="recurrence-options"
                        name="recurrence-options"
                      >
                        {selectedRecurringType === 8 ? (
                          <>
                            <FormControlLabel
                              value="1"
                              control={<Radio color="primary" />}
                              label={t(
                                `Createjob:Repeat on the ${getOrdinalDay(
                                  Number(moment(monthlyYearlyDate).format("DD"))
                                )} of every month`
                              )}
                            />
                            <FormControlLabel
                              value="2"
                              control={<Radio color="primary" />}
                              label={t(
                                `Createjob:Repeat on the ${getOrdinalWeek(
                                  Math.ceil(
                                    Number(
                                      moment(monthlyYearlyDate).format("DD")
                                    ) / 7
                                  )
                                )} ${getDayName(
                                  moment(monthlyYearlyDate).day()
                                )} of every month`
                              )}
                            />
                          </>
                        ) : (
                          <>
                            <FormControlLabel
                              value="1"
                              control={<Radio color="primary" />}
                              label={t(
                                `Createjob:Repeat on the ${getOrdinalDay(
                                  Number(moment(monthlyYearlyDate).format("DD"))
                                )} ${getMonthName(
                                  moment(monthlyYearlyDate).month()
                                )} every year`
                              )}
                            />
                            <FormControlLabel
                              value="2"
                              control={<Radio color="primary" />}
                              label={t(
                                `Createjob:Repeat on the ${getOrdinalWeek(
                                  Math.ceil(
                                    Number(
                                      moment(monthlyYearlyDate).format("DD")
                                    ) / 7
                                  )
                                )} ${getDayName(
                                  moment(monthlyYearlyDate).day()
                                )} of ${getMonthName(
                                  moment(monthlyYearlyDate).month()
                                )} every year`
                              )}
                            />
                          </>
                        )}
                      </RadioGroup>
                    </Grid>
                  </Grid>
                ) : selectedRecurringType === 10 ? (
                  <Grid item xs={12}>
                    <Grid
                      container
                      spacing={2}
                      direction="column"
                      alignItems="flex-start"
                      style={{ marginTop: "4px", marginBottom: "0.5rem" }}
                    >
                      <Grid item xs={6} className={"CreateJob__repeatOn"}>
                        <div
                          style={{
                            marginTop: 0,
                            marginBottom: "0.5rem",
                          }}
                          className={"CreateJob__repeatOn__repeat-heading"}
                        >
                          {t("Createjob:Repeat On")} :
                        </div>

                        {repeatDays.map((day) => (
                          <span
                            onClick={() =>
                              updateSelectedFortnightlyDays(day.id)
                            }
                            className={
                              selectedFortnightlyDays[day.id]
                                ? "CreateJob__repeatOn__repeat-days-selected"
                                : "CreateJob__repeatOn__repeat-days"
                            }
                            key={day.id}
                          >
                            {day.name}
                          </span>
                        ))}
                      </Grid>
                      <Grid item xs={6} style={{ marginTop: "3px" }}>
                        <Typography
                          variant="caption"
                          className={
                            "CreateJob__form__recurring__instruction-text"
                          }
                        >
                          * Jobs will be created on this weekday every 2 weeks
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : null}

                <Grid
                  className={"CreateJob__repeatEnd"}
                  // style={{ marginLeft: "10px" }}
                  item
                  xs={6}
                >
                  <div className={"CreateJob__repeatOn__repeat-heading"}>
                    {t("Createjob:Recurrence end criteria")} :
                  </div>
                  <RadioGroup
                    value={scheduleEndCriteria}
                    onChange={handleScheduleCriteriaChange}
                    row
                    aria-label="position"
                    style={{ marginLeft: "-12px" }}
                    name="position"
                  >
                    <Radio id="recurring-end-date" color="primary" value="0" />
                    <label
                      htmlFor="recurring-end-date"
                      className={"CreateJob__repeatOn__schedule-criteria-label"}
                    >
                      {t("Createjob:End date")}
                    </label>
                    <Radio
                      id="no-of-occurrences"
                      name="no-of-occurrences"
                      color="primary"
                      value="1"
                    />
                    <label
                      htmlFor="no-of-occurrences"
                      className={"CreateJob__repeatOn__schedule-criteria-label"}
                    >
                      {t("Createjob:No. of occurrences")}
                    </label>
                    {/* <FormControlLabel
                    value="0"
                    control={<Radio color="primary" />}
                    label="End date"
                  />
                  <FormControlLabel
                    value="1"
                    control={<Radio color="primary" />}
                    label="Ends after no of occurences"
                  /> */}
                  </RadioGroup>
                  <Grid item xs={12}>
                    {/* {endDate} */}
                    {scheduleEndCriteria == 0 ? (
                      <div style={{ marginTop: "6px" }}>
                        <DateTimePickerInput
                          label={t("Createjob:End date")}
                          type={2}
                          className={
                            "CreateJob__form__task-accordion__date-input"
                          }
                          showIcon={true}
                          disablePast={true}
                          value={endDate}
                          onChange={(value: any, format: any) => {
                            const newDate = new Date(value);
                            newDate.setHours(23);
                            newDate.setMinutes(59);
                            newDate.setSeconds(59);
                            setEndDate(
                              DateTimeFormatter.changeOnlyFormatInSameTimeZone(
                                newDate,
                                "YYYY-MM-DD"
                              )
                            );
                            updateRepeatObj({
                              key: "endDate",
                              value: newDate,
                            });
                          }}
                        ></DateTimePickerInput>
                      </div>
                    ) : (
                      <div
                        className={
                          "CreateJob__repeatOn__schedule-no-of-occurences"
                        }
                      >
                        <Input
                          type="text"
                          id="occurence"
                          value={occurrences}
                          name="occurrences"
                          label={t("Createjob:No. of occurrences")}
                          onchange={(evt: any) => {
                            setOccurrences(evt.target.value);
                            updateRepeatObj({
                              key: "occurrences",
                              value: evt.target.value,
                            });
                          }}
                          onkeypress={() => {}}
                        />
                      </div>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              {/* </AccordionDetails>
          </Accordion> */}
            </div>
          </>
        ) : null}
      </div>
    </>
  );
};

export default CreateRecurringJob;
